import { debounce } from '../../scripts/utils';

type TDocument = {
    content: string;
    group: string;
    link: string;
    previewImage: string;
    title: string;
    type: string;
};

type TSuggestions = {
    didSecondSearch: boolean;
    documents: TDocument[];
    suggestion: string;
    suggestions: { [key: string]: number };
};

const searchKeywordClass = 'c-search-keyword';
const listboxClass = searchKeywordClass + '__listbox';
const listboxOpenClass = listboxClass + '--open';
const itemClass = searchKeywordClass + '__item';
const itemActiveClass = itemClass + '--active';
const itemId = 'search-keyword-suggestion-';
const highlightClass = searchKeywordClass + '__highlight';

const searchKeywordJsClass = 'js-search-keyword';
const inputJsClass = searchKeywordJsClass + '-input';
const listboxJsClass = searchKeywordJsClass + '-listbox';
const itemJsClass = searchKeywordJsClass + '-item';
const submitJsClass = searchKeywordJsClass + '-submit';

const inputElement = document.querySelector('.' + inputJsClass) as HTMLInputElement;
const listboxElement = document.querySelector('.' + listboxJsClass) as HTMLInputElement;
const submitElement = document.querySelector('.' + submitJsClass) as HTMLInputElement;
let itemElements = [];

const minimumInputLength = 3;

let activeSuggestionIndex = null;

let contentUid: string;
let url: string;
let updating = false;
let listboxHover = false;

if (inputElement && listboxElement && submitElement) {
    contentUid = inputElement.dataset.contentUid;
    url = inputElement.dataset.suggest;

    // Add focus event listener to input
    inputElement.addEventListener('focus', () => {
        openListbox();
    });

    // Add blur event listener to input
    inputElement.addEventListener('blur', () => {
        // If the cursor is not hover the listbox
        // (If the cursor is hover the listbox, an option has been selected and the click event listener will take over)
        if (!listboxHover) {
            closeListbox();
        }
    });

    // Add input event listener to input
    inputElement.addEventListener('input', () => {
        // If the value of the input is not long enough
        if (inputElement.value.length < minimumInputLength) {
            closeListbox();
            return;
        }

        // If the listbox is closed
        if (!listboxElement.classList.contains(listboxOpenClass)) {
            openListbox();
        }

        debounce(updateSuggestions)();
    });

    // Add a keydown event listener to input
    inputElement.addEventListener('keydown', (event) => {
        // If the tab key is pressed
        if (
            event.key === 'Tab'
            && !event.ctrlKey
            && !event.altKey
            && !event.metaKey
            && !event.shiftKey
        ) {
            event.preventDefault();

            // If the listbox is open
            if (listboxElement.classList.contains(listboxOpenClass)) {
                closeListbox();
            }

            // Focus on the submit button
            submitElement.focus();
        }

        // If the escape key is pressed
        if (
            event.key === 'Escape'
            && !event.ctrlKey
            && !event.altKey
            && !event.metaKey
            && !event.shiftKey
        ) {
            // If the listbox is open
            if (listboxElement.classList.contains(listboxOpenClass)) {
                closeListbox();
            } else {
                // Clear input
                inputElement.value = '';
            }
        }

        // If the enter key is pressed
        if (
            event.key === 'Enter'
            && !event.ctrlKey
            && !event.altKey
            && !event.metaKey
            && !event.shiftKey
        ) {
            // Prevent form submit
            event.preventDefault();

            // If the listbox is open
            if (listboxElement.classList.contains(listboxOpenClass)) {
                // If a suggestion is active
                if (activeSuggestionIndex !== null) {
                    // Set input value
                    inputElement.value = itemElements[activeSuggestionIndex].dataset.value;
                }

                closeListbox();
            } else {
                submitElement.click();
            }
        }

        // If the alt and down arrow keys are pressed
        if (
            event.key === 'ArrowDown'
            && !event.ctrlKey
            && event.altKey
            && !event.metaKey
            && !event.shiftKey
        ) {
            openListbox();
        }

        // If the down arrow key is pressed
        if (
            event.key === 'ArrowDown'
            && !event.ctrlKey
            && !event.altKey
            && !event.metaKey
            && !event.shiftKey
        ) {
            // If the listbox is not open
            if (!listboxElement.classList.contains(listboxOpenClass)) {
                openListbox();
            }

            // If there is no selected suggestion or if the selected suggestion is last in the list
            if (activeSuggestionIndex === null || activeSuggestionIndex === itemElements.length - 1) {
                // Select the first suggestion
                updateActiveSuggestion(0);
            } else {
                // Select the next suggestion
                updateActiveSuggestion(activeSuggestionIndex + 1);
            }
        }

        // If the alt and up arrow keys are pressed
        if (
            event.key === 'ArrowUp'
            && !event.ctrlKey
            && event.altKey
            && !event.metaKey
            && !event.shiftKey
        ) {
            closeListbox();
        }

        // If the up arrow key is pressed
        if (
            event.key === 'ArrowUp'
            && !event.ctrlKey
            && !event.altKey
            && !event.metaKey
            && !event.shiftKey
        ) {
            event.preventDefault();

            // If the listbox is not open
            if (!listboxElement.classList.contains(listboxOpenClass)) {
                openListbox();
            }

            // If there is no selected suggestion or if the selected suggestion is first in the list
            if (activeSuggestionIndex === null || activeSuggestionIndex === 0) {
                // Select the last suggestion
                updateActiveSuggestion(itemElements.length - 1);
            } else {
                // Select the previous suggestion
                updateActiveSuggestion(activeSuggestionIndex - 1);
            }
        }
    });

    // Add mouseenter event listener to listbox
    listboxElement.addEventListener('mouseenter', () => {
        // Update flag
        listboxHover = true;
    });

    // Add mouseleave event listener to listbox
    listboxElement.addEventListener('mouseleave', () => {
        // Update flag
        listboxHover = false;
    });

    updateSuggestions();
}

function openListbox (): void {
    if (!inputElement || !listboxElement) {
        return;
    }

    // If the listbox is already open
    if (listboxElement.classList.contains(listboxOpenClass)) {
        return;
    }

    // If the value of the input is long enough
    if (inputElement.value.length >= minimumInputLength) {
        // Add active class
        listboxElement.classList.add(listboxOpenClass);

        // Update aria-expanded
        inputElement.ariaExpanded = 'true';
    }
}

function closeListbox (): void {
    if (!inputElement || !listboxElement) {
        return;
    }

    // If the listbox is already closed
    if (!listboxElement.classList.contains(listboxOpenClass)) {
        return;
    }

    // Remove active class
    listboxElement.classList.remove(listboxOpenClass);

    // Update aria-expanded
    inputElement.ariaExpanded = 'false';

    // Reset active suggestion index
    updateActiveSuggestion(null);

    // Remove aria-activedescendant
    inputElement.removeAttribute('aria-activedescendant');
}

function updateActiveSuggestion (index: number): void {
    if (!inputElement) {
        return;
    }

    // Update active suggestion index
    activeSuggestionIndex = index;

    // Stop if there are no suggestions
    if (!itemElements || itemElements.length === 0) {
        return;
    }

    // For each suggestion
    itemElements.forEach((itemElement: HTMLElement, index) => {
        // If the suggestion is active
        if (index === activeSuggestionIndex) {
            // Add active class to item
            itemElement.classList.add(itemActiveClass);
            // Update aria-selected
            itemElement.ariaSelected = 'true';
            // Focus element
            itemElement.focus();
            // Set aria-activedescendant
            inputElement.setAttribute('aria-activedescendant', itemElement.id);
        } else {
            // Remove active class from item
            itemElement.classList.remove(itemActiveClass);
            // Update aria-selected
            itemElement.ariaSelected = 'false';
        }
    });
}

async function updateSuggestions (): Promise<void> {
    if (!inputElement || !listboxElement) {
        return;
    }

    // Check if an update is already in progress
    if (updating) {
        return;
    } else {
        // Update flag
        updating = true;
    }

    // Clear suggestions
    listboxElement.innerHTML = '';
    itemElements = [];

    // If the value of the input is long enough
    if (inputElement.value.length >= minimumInputLength) {
        // Fetch suggestions
        const suggestions = await fetchSuggestions(inputElement.value);

        // If there are no suggestions
        if (!suggestions || !suggestions.suggestions) {
            // Update flag
            updating = false;
            closeListbox();
            return;
        }

        // For each suggestion
        Object.keys(suggestions.suggestions)?.forEach((item, index) => {
            // Create item element
            const itemElement = document.createElement('li');
            // Add id to item
            itemElement.id = itemId + index;
            // Add classes to item
            itemElement.classList.add(itemJsClass, itemClass);
            // Add role to item
            itemElement.role = 'option';
            // Add data-attribute to item
            itemElement.setAttribute('data-value', item);
            // Add aria-label to item
            itemElement.ariaLabel = item;
            // Add click event listener
            itemElement.addEventListener('click', () => {
                // Update input value
                inputElement.value = itemElement.dataset.value;

                updateSuggestions();

                closeListbox();
            });

            // Add item to array
            itemElements.push(itemElement);

            // Get value from input and trim it
            const trimmedInput = inputElement.value.trim();

            // Create span element for highlighted text
            const spanElement = document.createElement('span');
            // Add class to span
            spanElement.classList.add(highlightClass);
            // Add input value to span
            spanElement.innerHTML = trimmedInput;

            // Add span to item
            itemElement.appendChild(spanElement);
            // Add remaining text to item (what is not already in the span)
            itemElement.innerHTML += item.substring(trimmedInput.length, item.length);

            // Add item to listbox
            listboxElement.appendChild(itemElement);
        });
    }

    // Update flag
    updating = false;
}

async function fetchSuggestions (input: string): Promise<TSuggestions> {
    if (!contentUid || !url) {
        return;
    }

    // Get value from input and trim it
    const trimmedInput = input.trim();

    // Create url with contentUid and input
    const requestUrl = `${url}&tx_projectsolr_suggest[contentUid]=${contentUid}&tx_projectsolr_suggest[queryString]=${trimmedInput}`;

    // Fetch url
    const response = await fetch(requestUrl);

    // Return JSON
    return await response.json();
}